import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx';
import { notifyAuthChange, useAuthContext } from './hooks/use-auth-context.ts';
import { setAuthChangeNotifier } from './utils/router-auth.ts';
import './styles.css';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Initialize auth change notifier for reactive updates
setAuthChangeNotifier(notifyAuthChange);

// Create a new router instance
const TanStackQueryProviderContext = TanStackQueryProvider.getContext();

// Router initialized with undefined! per TanStack Router best practices
// Auth will be provided via RouterProvider context prop
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
    auth: undefined! // Will be provided by InnerApp via RouterProvider context
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Initialize Sentry for error tracking

Sentry.init({
  enabled: !import.meta.env.DEV,
  dsn: 'https://89de63dabd52407f8833aa2cca022525@o4510079024889856.ingest.de.sentry.io/4510318255669328',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration()
  ],
  // Tracing
  tracesSampleRate: 0.5, //  Capture 50% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  // Enable logs to be sent to Sentry
  enableLogs: true
});

/**
 * Inner app component that provides reactive auth context to the router
 * This follows the TanStack Router recommended pattern for authentication
 */
function InnerApp() {
  // Subscribe to auth changes via useSyncExternalStore
  const auth = useAuthContext();

  return (
    <RouterProvider
      router={router}
      context={{
        ...TanStackQueryProviderContext,
        auth // Reactive auth context - updates automatically on login/logout
      }}
    />
  );
}

/**
 * Main app component with all providers
 */
function App() {
  return (
    <StrictMode>
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
          <InnerApp />
        </TanStackQueryProvider.Provider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
}

// Render the app
const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
